import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle, X, ArrowClockwise, Certificate, Bell, Trash,
} from "@phosphor-icons/react";

const LEVELS = ["expert", "trusted", "verified", "institutional", "fellow"];

export default function ExpertsTab() {
  const [apps, setApps] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [status, setStatus] = useState("pending");
  const [busy, setBusy] = useState(false);
  const [decisionFor, setDecisionFor] = useState(null);
  const [decision, setDecision] = useState({ action: "approve", level: "expert", reason: "" });

  const load = async () => {
    try {
      const [a, n] = await Promise.all([
        http.get("/admin/expert-applications", { params: { status, limit: 100 } }),
        http.get("/admin/expert-notifications", { params: { unread_only: true, limit: 60 } }),
      ]);
      setApps(a.data.applications || []);
      setNotifs(n.data.notifications || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Load failed");
    }
  };
  useEffect(() => { load(); }, [status]);

  const submitDecision = async () => {
    if (!decisionFor) return;
    setBusy(true);
    try {
      await http.post(`/admin/expert-applications/${decisionFor.id}/decide`, decision);
      toast.success(`Application ${decision.action}d`);
      setDecisionFor(null);
      setDecision({ action: "approve", level: "expert", reason: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const markAllRead = async () => {
    try {
      await http.post("/admin/expert-notifications/mark-all-read");
      toast.success("Notifications cleared");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-experts-tab">
      {/* Notifications */}
      <section className="rounded-2xl border border-black/10 bg-white p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-serif-display text-xl flex items-center gap-2"><Bell size={18} weight="fill" /> Expert notifications ({notifs.length})</h3>
          <div className="flex gap-2">
            <button onClick={load} data-testid="experts-tab-refresh" className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:bg-black/5 inline-flex items-center gap-1"><ArrowClockwise size={10} weight="bold" /> Refresh</button>
            {notifs.length > 0 && (
              <button onClick={markAllRead} data-testid="experts-tab-mark-all-read" className="rounded-full bg-[#151312] text-[#F5EBDC] px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase hover:opacity-90 inline-flex items-center gap-1"><Trash size={10} weight="bold" /> Mark all read</button>
            )}
          </div>
        </div>
        <div className="mt-3 space-y-1.5 max-h-72 overflow-auto">
          {notifs.length === 0 && <div className="text-sm text-[#3A3830]">Nothing new.</div>}
          {notifs.map((n) => (
            <div key={n.id} data-testid={`notif-${n.id}`} className="rounded-lg border border-black/10 bg-[#FBF2E1]/40 px-3 py-2 text-xs">
              <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">{n.kind}</div>
              <div className="mt-1 text-[#3A3830]">
                {n.title || n.name || n.email || n.expert_name}
                {n.moderation_status && <span className="ml-2 text-[#3A3830]">· {n.moderation_status}</span>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Applications */}
      <section className="rounded-2xl border border-black/10 bg-white p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-serif-display text-xl">Expert applications</h3>
          <div className="flex gap-1.5">
            {["pending", "approved", "rejected", "all"].map((s) => (
              <button key={s} onClick={() => setStatus(s)} data-testid={`experts-tab-status-${s}`} className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase border ${
                status === s ? "border-transparent bg-[color:var(--np-brand,#B85F2A)] text-white" : "border-black/15 bg-white hover:bg-black/5"
              }`}>{s}</button>
            ))}
          </div>
        </div>
        <div className="mt-3 space-y-2">
          {apps.length === 0 && <div className="text-sm text-[#3A3830]">No {status} applications.</div>}
          {apps.map((a) => (
            <div key={a.id} data-testid={`app-${a.id}`} className="rounded-lg border border-black/10 p-3 bg-white">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-serif-display text-lg">{a.full_name} <span className="text-xs text-[#3A3830]">· {a.profession}</span></div>
                  <div className="text-xs text-[#3A3830] mt-0.5">{a.email}{a.institution ? ` · ${a.institution}` : ""}</div>
                  {a.expertise?.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {a.expertise.map((t) => <span key={t} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">{t}</span>)}
                    </div>
                  )}
                  <p className="mt-2 text-xs text-[#3A3830] line-clamp-3 whitespace-pre-wrap">{a.biography}</p>
                  {a.external_links?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {a.external_links.map((l) => <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[color:var(--np-brand,#B85F2A)] underline">{l.label}</a>)}
                    </div>
                  )}
                </div>
                {status === "pending" && (
                  <div className="flex flex-col gap-1.5">
                    <button onClick={() => { setDecisionFor(a); setDecision({ action: "approve", level: "expert", reason: "" }); }} data-testid={`approve-${a.id}`} className="rounded-full bg-emerald-600 text-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-1"><CheckCircle size={11} weight="fill" /> Approve</button>
                    <button onClick={() => { setDecisionFor(a); setDecision({ action: "reject", level: "expert", reason: "" }); }} data-testid={`reject-${a.id}`} className="rounded-full bg-white border border-black/15 px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase inline-flex items-center gap-1"><X size={11} weight="bold" /> Reject</button>
                  </div>
                )}
                {a.status !== "pending" && (
                  <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] whitespace-nowrap">
                    {a.status === "approved" ? <><Certificate size={11} weight="fill" className="inline" /> Approved</> : a.status}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Decision modal */}
      {decisionFor && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center px-4" data-testid="decision-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl text-[#151312]">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              {decision.action === "approve" ? "Approve expert" : decision.action === "reject" ? "Reject application" : "Update"}
            </div>
            <h3 className="mt-1 font-serif-display text-2xl">{decisionFor.full_name}</h3>
            {decision.action === "approve" && (
              <div className="mt-4">
                <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Level</label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {LEVELS.map((L) => (
                    <button key={L} onClick={() => setDecision({ ...decision, level: L })} data-testid={`decision-level-${L}`} className={`rounded-full px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase border ${decision.level === L ? "border-transparent bg-[color:var(--np-brand,#B85F2A)] text-white" : "border-black/15 bg-white"}`}>
                      {L}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-3">
              <label className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)]">Note (internal, optional)</label>
              <textarea rows={3} value={decision.reason} onChange={(e) => setDecision({ ...decision, reason: e.target.value })} data-testid="decision-reason" className="mt-1 w-full bg-white border border-black/15 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setDecisionFor(null)} className="rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase">Cancel</button>
              <button onClick={submitDecision} disabled={busy} data-testid="decision-confirm" className="rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase disabled:opacity-50">
                {busy ? "Working…" : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
